import {
  isPrivateFeature,
  isWaterbody,
  isWaterAccessSite,
  isPolygonFeature,
  isFishingArea,
  isPublicFeature,
  isFishingRegion,
  isWaterbodyBadge,
  isMarkupFeature,
  isSearchResultFeature,
  isEventClick,
  isCatchPolygon,
} from '@/lib/feature-identification'
import { BASE, OFFLINE_MAPS_SOURCE } from '@/lib/enums'
import {
  createRule,
  type ClassificationRuleType,
} from '@/lib/map-click-handler/types'
import { Vertical } from '@/types/verticals'

/**
 * Manage rules for classifying a feature
 * If a feature matches its given test, it will be added to the `target` array within classify-features
 * These classifications can also modify the `state` object.
 * If you only need to modify state without appending to a target array, return false
 *
 * NOTE: The `createRule` wrapper is a TS helper to infer the destructered params without more verbose typing
 */

// Shared
const sharedClassifications: ClassificationRuleType[] = [
  createRule({
    test: ({ feature, state }) => {
      if (
        feature.source === BASE &&
        feature.properties?.['data:type'] === 'state'
      ) {
        state.stateClicked =
          feature.properties['data:short_name']?.toLowerCase()
        return false
      }
      return false
    },
  }),
  createRule({
    test: ({ feature }) => isMarkupFeature(feature),
    target: 'markupsClicked',
  }),
  createRule({
    test: ({ feature }) => feature.source === OFFLINE_MAPS_SOURCE,
    target: 'offlineMapClicked',
  }),
  createRule({
    test: ({ feature, state }) => {
      if (isSearchResultFeature(feature)) {
        state.multiQueryOverride = true
        return true
      }
      return false
    },
    target: 'searchResultsClicked',
  }),
  createRule({
    test: ({ feature }) =>
      isPrivateFeature(feature) && isPolygonFeature(feature),
    target: 'privateParcelsClicked',
  }),
  createRule({
    test: ({ feature }) =>
      isPublicFeature(feature) && isPolygonFeature(feature),
    target: 'publicParcelsClicked',
  }),
]

// Fish
const fishClassifications: ClassificationRuleType[] = [
  createRule({
    test: ({ feature }) => isEventClick(feature),
    target: 'eventClicked',
  }),
  createRule({
    test: ({ feature }) => isWaterbodyBadge(feature),
    target: 'waterbodyBadgesClicked',
  }),
  createRule({
    test: ({ feature }) => isWaterAccessSite(feature),
    target: 'waterAccessClicked',
  }),
  createRule({
    test: ({ feature }) => isFishingArea(feature),
    target: 'fishingAreaClicked',
  }),
  createRule({
    test: ({ feature }) => isCatchPolygon(feature),
    target: 'catchPolygonClicked',
  }),
  createRule({
    test: ({ feature }) => isFishingRegion(feature) || isWaterbody(feature),
    target: 'waterbodyClicked',
  }),
]

export const classificationRules: Record<
  Vertical.Fish | 'shared',
  ClassificationRuleType[]
> = {
  [Vertical.Fish]: fishClassifications,
  shared: sharedClassifications,
}
